import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { Button } from 'foundations-components/transitional/components/Button';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsStandardizedInstallQuery } from '../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/MarketplaceListingDetailsRpc-app.hooks';
import { FeatureCompatibilityModal } from './compatibility/FeatureCompatibilityModal';
import { DetailsPageContext } from './context/DetailsPageContext';
import { DetailsError } from './error/DetailsError';
import { useInstallCompatibility } from './hooks/useInstallCompatibility';
import { useListingPreview } from './hooks/useListingPreview';
import { useTracking } from './hooks/useTracking';
import { useUnifiedDetails } from './hooks/useUnifiedDetails';
import { DetailsPage } from './layout/DetailsPage';
import { ProductBuyBox } from './layout/cards/ProductBuyBox';
import { ProductFeaturesCard } from './layout/cards/ProductFeaturesCard';
import { ProductFeedbackCard } from './layout/cards/ProductFeedbackCard';
import { ProductFunctionalityCard } from './layout/cards/ProductFunctionalityCard';
import { ProductInfoCard } from './layout/cards/ProductInfoCard';
import { ProductOverviewCard } from './layout/cards/ProductOverviewCard';
import { ProductPricingCard } from './layout/cards/ProductPricingCard';
import { ProductRequirementsCard } from './layout/cards/ProductRequirementsCard';
import { ProductReviewsCard } from './layout/cards/ProductReviewsCard';
import { generateLeadWithAction } from './leadGen/leadGenClient';
import { HSPageViewTracker } from './utils/HSPageViewTracker';

// @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'bender-url!ecosystem-marketpla... Remove this comment to see the full error message
import fallbackAgentOverviewImage from 'bender-url!marketplace-ui-details/img/agent-overview-fallback.png';
import { getAppInstallType } from './utils/getAppInstallType';
import { MarketplaceListingCTAButton } from './install/MarketplaceListingCTAButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function Details({
  appContext,
  logInUrl,
  // To be used in the Public Marketplace when the CTA reads "Sign in"
  previewListingId,
  // Will be used to fetch listing preview data
  language,
  renderedErrorLink,
  slug,
  userInfo,
  isEmbedded = false,
  onEmbedClose,
  onInstallComplete,
  onInstallError
}) {
  const published = !!slug && !previewListingId;
  const isPreview = !!previewListingId;
  const [compatibilityModalOpen, setCompatibilityModalOpen] = useState(false);
  const {
    pathname
  } = useLocation();

  // This will be the only call to Unified Details Service
  // Once everything is migrated to V3
  const {
    unifiedDetailsData: unifiedDetailsDataV3,
    unifiedDetailsLoading: unifiedDetailsLoadingV3,
    unifiedDetailsError: unifiedDetailsErrorV3
  } = useUnifiedDetails({
    appContext,
    slug: slug || '',
    language: getLang(),
    skip: !slug || isPreview,
    version: 3
  });

  // Additional call to Unified Details Service V2
  // To supplement with App fields that are not available in V3
  const {
    unifiedDetailsData: unifiedDetailsDataV2,
    unifiedDetailsLoading: unifiedDetailsLoadingV2,
    unifiedDetailsError: unifiedDetailsErrorV2
  } = useUnifiedDetails({
    appContext,
    slug: slug || '',
    language: getLang(),
    skip: !slug || isPreview,
    version: 2
  });
  const {
    previewData,
    previewLoading,
    previewError
  } = useListingPreview({
    appContext,
    language: language || getLang(),
    listingId: previewListingId,
    skip: !!slug || !isPreview
  });
  const unifiedDetailsRequestStatusV3 = getDataFetchingClientRequestState(unifiedDetailsErrorV3, unifiedDetailsLoadingV3);
  const unifiedDetailsRequestStatusV2 = getDataFetchingClientRequestState(unifiedDetailsErrorV2, unifiedDetailsLoadingV2);
  const previewRequestStatus = getDataFetchingClientRequestState(previewError, previewLoading);

  // UDS V3 data from Framework
  const {
    agencyPricingUrl,
    betaDetails,
    builtByHubSpot,
    connectionType,
    caseStudyUrl,
    category,
    certifiedAt,
    companyName,
    companyUrl,
    contactForm,
    currency,
    facebookUrl,
    features: frameworkFeaturesV3,
    installCount,
    languages,
    listingIcon,
    listingId,
    liveChatUrl,
    meetingsUrl,
    name,
    overview,
    isDashworks,
    isOpsHub,
    partnerPortalId,
    phone,
    pricingPlans,
    pricingPlansUrl,
    privacyUrl1,
    privacyUrl2,
    privacyUrl3,
    productFeatures: frameworkProductFeatures,
    productType,
    screenshots: frameworkScreenshots,
    setupDocsUrl,
    sharedDataInfo,
    sharedDataHow,
    sharedObjects,
    // New V3 sharedObjects data
    sourceId,
    supportEmail,
    supportLanguages,
    supportUrl,
    tagline,
    termsUrl,
    ubbInfo,
    urlSlug,
    videos: frameworkVideos
  } = previewData || unifiedDetailsDataV3;

  // Remaining App Details data from Unified Details Service V2
  const {
    id,
    integrationFeatures,
    neededScopeGroups,
    offeringId,
    tiers
  } = previewData || unifiedDetailsDataV2;

  // Standardized install gate check via RPC (for STANDARD connectionType)
  const {
    data: standardizedInstallData
  } = useIsStandardizedInstallQuery({
    variables: {
      listingId: listingId || 0
    },
    skip: !listingId || appContext !== 'app' || productType !== 'APP' || connectionType !== 'STANDARD'
  });
  const isUngatedToStandardInstall = standardizedInstallData === null || standardizedInstallData === void 0 ? void 0 : standardizedInstallData.standardizedInstall;
  const {
    data: installCompatibilityData
  } = useInstallCompatibility({
    appContext,
    listingId,
    published
  });
  const screenshots = useMemo(() => {
    // Filter out screenshot objects with empty values
    const screenshotsWithValues = frameworkScreenshots === null || frameworkScreenshots === void 0 ? void 0 : frameworkScreenshots.filter(s => s.screenshot.value);
    return screenshotsWithValues !== null && screenshotsWithValues !== void 0 && screenshotsWithValues.length ? screenshotsWithValues :
    // Default to agent fallback image
    productType === 'AGENT' ? [{
      screenshot: {
        value: fallbackAgentOverviewImage,
        altText: ''
      }
    }] : [];
  }, [frameworkScreenshots, productType]);
  const videos = useMemo(() => {
    // Filter out video objects with empty values
    return frameworkVideos === null || frameworkVideos === void 0 ? void 0 : frameworkVideos.filter(s => s.video.value);
  }, [frameworkVideos]);
  const featuresV3 = useMemo(() => {
    // Filter out feature objects with empty values
    return frameworkFeaturesV3 === null || frameworkFeaturesV3 === void 0 ? void 0 : frameworkFeaturesV3.filter(feature => feature.featureName);
  }, [frameworkFeaturesV3]);
  const tools = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.data.isTool);
  }, [frameworkProductFeatures]);
  const appObjects = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'APP_OBJECT');
  }, [frameworkProductFeatures]);
  const appCards = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'UIE' &&
    // if any of these fields are missing, don't render the card
    feature.name && feature.data.description && feature.data.previewImage && feature.data.eligibleLocations);
  }, [frameworkProductFeatures]);
  const appInstallType = getAppInstallType({
    connectionType,
    isDashworks,
    isOpsHub,
    isUngatedToStandardInstall
  });
  const {
    trackInstall,
    trackInteraction,
    trackPageViewed
  } = useTracking({
    appContext,
    companyName,
    embed: isEmbedded,
    isPreview,
    listingId,
    slug: urlSlug,
    sourceId,
    type: productType
  });
  useEffect(() => {
    if (isEmbedded) {
      //page view already tracked on storefront for non-embedded implementation
      trackPageViewed();
    }
  }, [isEmbedded, trackPageViewed]);
  const runLeadGen = useCallback(({
    action
  }) => {
    if (productType !== 'APP' || appContext !== 'app' || builtByHubSpot || !published) {
      return;
    }
    trackInteraction({
      action: 'generated lead',
      value: `${partnerPortalId}`
    });
    if (action && offeringId) {
      generateLeadWithAction({
        action,
        offeringId
      }).catch(error => {
        console.error('Error sending lead:', error);
      });
    }
  }, [appContext, builtByHubSpot, offeringId, partnerPortalId, productType, published, trackInteraction]);

  // This info will be returned from the Unified Installability Service
  // But for now, we're using the separate apps-specific endpoint/hook
  // To get real data for the page

  const renderedPrimaryCTA = useMemo(() => {
    // Basic disabled button for preview
    // Note: Apps with STANDARD installType are allowed to be previewed
    if (isPreview && (productType === 'AGENT' || productType === 'APP' && appInstallType !== 'STANDARD')) {
      return /*#__PURE__*/_jsx(Button, {
        use: "primary",
        disabled: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.install.${productType}`
        })
      });
    }

    // Public "Sign in" buttons that redirect to portal picker
    if (appContext === 'public') {
      return /*#__PURE__*/_jsx(Button, {
        use: "primary",
        href: logInUrl,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.signIn.${productType}`
        })
      });
    }
    if (productType === 'APP' || productType === 'AGENT') {
      return /*#__PURE__*/_jsx(MarketplaceListingCTAButton, {
        appContext: appContext,
        buttonProps: {
          onEmbedClose,
          onInstallComplete,
          onInstallError
        },
        isEmbedded: isEmbedded,
        previewListingId: previewListingId,
        slug: urlSlug,
        userInfo: userInfo
      });
    }
    return null;
  }, [appContext, appInstallType, isEmbedded, isPreview, logInUrl, onEmbedClose, onInstallComplete, onInstallError, previewListingId, productType, urlSlug, userInfo]);
  const unifiedDetailsError = unifiedDetailsErrorV2 || unifiedDetailsErrorV3;
  if (unifiedDetailsError) {
    return /*#__PURE__*/_jsx(DetailsError, {
      renderedErrorLink: renderedErrorLink,
      unifiedDetailsError: unifiedDetailsError
    });
  }
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: [unifiedDetailsRequestStatusV2, unifiedDetailsRequestStatusV3, previewRequestStatus],
    children: /*#__PURE__*/_jsxs(DetailsPageContext.Provider, {
      value: {
        appContext,
        iconUrl: listingIcon === null || listingIcon === void 0 ? void 0 : listingIcon.value,
        listingId,
        listingIdV2: id,
        // TODO - deprecate --- this is still needed for sendAdminEmail
        name,
        productType,
        published,
        runLeadGen,
        trackInteraction,
        trackInstall,
        userInfo,
        isEmbedded
      },
      children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(HSPageViewTracker, {
        pathname: pathname,
        overrideTrack: appContext === 'app',
        addLang: appContext === 'app'
      }), /*#__PURE__*/_jsx(DetailsPage, {
        leftColumn: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ProductBuyBox, {
            betaDetails: betaDetails,
            certifiedAt: certifiedAt,
            companyName: companyName || 'HubSpot',
            companyUrl: companyUrl,
            contactForm: contactForm,
            installCount: installCount,
            meetingsUrl: meetingsUrl,
            tagline: tagline,
            primaryCTA: renderedPrimaryCTA,
            productId: offeringId
          }), /*#__PURE__*/_jsx(ProductRequirementsCard, {
            integrationFeatures: integrationFeatures,
            installType: appInstallType,
            features: featuresV3,
            builtByHubSpot: builtByHubSpot,
            neededScopeGroups: neededScopeGroups,
            onCompatibilityModalOpen: () => setCompatibilityModalOpen(true),
            pricingPlans: pricingPlans || [],
            tiers: tiers || []
          })]
        }),
        rightColumn: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ProductOverviewCard, {
            caseStudyUrl: caseStudyUrl,
            overview: overview,
            screenshots: screenshots,
            setupDocsUrl: setupDocsUrl,
            videos: videos
          }), /*#__PURE__*/_jsx(ProductFeaturesCard
          // Memoized, sanitized features
          , {
            features: featuresV3,
            integrationFeatures: integrationFeatures,
            onCompatibilityModalOpen: () => setCompatibilityModalOpen(true),
            showCompatibility: productType !== 'AGENT' && !!installCompatibilityData && !(integrationFeatures !== null && integrationFeatures !== void 0 && integrationFeatures.length)
          }), /*#__PURE__*/_jsx(ProductFunctionalityCard, {
            sharedDataHow: sharedDataHow,
            sharedDataInfo: sharedDataInfo,
            sharedObjects: sharedObjects,
            appObjects: appObjects,
            tools: tools,
            appCards: appCards
          }), productType !== 'AGENT' && /*#__PURE__*/_jsx(ProductReviewsCard, {
            isPreview: isPreview,
            partnerPortalId: partnerPortalId,
            productId: offeringId,
            supportUrl: supportUrl,
            userInfo: userInfo
          }), /*#__PURE__*/_jsx(ProductPricingCard, {
            agencyPricingUrl: agencyPricingUrl,
            currency: currency,
            pricingPlansUrl: pricingPlansUrl,
            pricingPlans: pricingPlans || [],
            ubbInfo: ubbInfo
          }), /*#__PURE__*/_jsx(ProductInfoCard, {
            details: {
              companyName: companyName || 'HubSpot',
              companyUrl,
              category,
              languages,
              installCount
            },
            support: {
              liveChatUrl,
              supportEmail,
              facebookUrl,
              builtByHubSpot,
              phone,
              supportLanguages,
              supportUrl
            },
            legal: {
              builtByHubSpot,
              privacyUrl1,
              privacyUrl2,
              privacyUrl3,
              termsUrl
            }
          }), productType === 'AGENT' && /*#__PURE__*/_jsx(ProductFeedbackCard, {
            partnerPortalId: partnerPortalId,
            logInUrl: logInUrl
          })]
        })
      }), compatibilityModalOpen && /*#__PURE__*/_jsx(FeatureCompatibilityModal, {
        features: featuresV3,
        listingId: listingId,
        onClose: () => setCompatibilityModalOpen(false)
      })]
    })
  });
}